import type { Permission } from "@/features/roles/constants/permissions";

import { assistantPath } from "@/config/constants";
import { ORG_ROLE, OrgRole, WORKSPACE_ROLE, WorkspaceRole } from "@/types/user";

// A route is open to the holders of its permission, whatever their role's
// base, as the backend's `@Can` is. Only a route without one falls back to a
// list of base roles.
type RoutePermission = { pattern: string } & (
  | { permission: Permission }
  | { allowedWorkspaceRoles: WorkspaceRole[] }
);

export type RouteAccess = {
  orgRole: OrgRole | null;
  workspaceRole: WorkspaceRole | null;
  permissions: Permission[];
};

const EVERYONE: WorkspaceRole[] = Object.values(WORKSPACE_ROLE);

/**
 * Route permissions configuration
 *
 * Rules:
 * - org:admin can access everything
 * - a route with a permission is open to whoever holds it
 * - any other route needs a workspace role in allowedWorkspaceRoles
 */
export const ROUTE_PERMISSIONS: RoutePermission[] = [
  {
    pattern: "/tutorials",
    allowedWorkspaceRoles: EVERYONE,
  },
  // Home - everyone lands here; the page branches by role
  {
    pattern: "/dashboard",
    allowedWorkspaceRoles: EVERYONE,
  },

  // Inbox - every conversation is a contact's, so it takes the contacts read
  {
    pattern: "/inbox",
    permission: "contacts.view",
  },

  // Profile - everyone can access
  {
    pattern: "/profile",
    allowedWorkspaceRoles: EVERYONE,
  },

  // Notifications - addressed to the viewer, so everyone who can sign in can read their own
  {
    pattern: "/notifications",
    allowedWorkspaceRoles: EVERYONE,
  },

  // Contacts
  {
    pattern: "/contacts",
    permission: "contacts.view",
  },

  // Tickets
  {
    pattern: "/tickets",
    permission: "tickets.view",
  },

  // Broadcasts, Ads, Workflows, Channels and Reports
  {
    pattern: "/broadcasts/create",
    permission: "broadcasts.edit",
  },
  {
    pattern: "/broadcasts",
    permission: "broadcasts.view",
  },

  {
    pattern: "/ads",
    permission: "ads.view",
  },

  {
    pattern: "/workflows/create",
    permission: "workflows.edit",
  },
  {
    pattern: "/workflows",
    permission: "workflows.view",
  },

  // First match wins, so connecting sits above the channels page it lives under.
  {
    pattern: "/channels/create",
    permission: "channels.connect",
  },
  {
    pattern: "/channels",
    permission: "channels.manage",
  },

  // Every figure on these two is money, so the backend gates them whole.
  {
    pattern: "/reports/sales",
    permission: "reports.amount",
  },
  {
    pattern: "/reports",
    permission: "reports.view",
  },

  // Integrations
  {
    pattern: "/integrations",
    permission: "integrations.manage",
  },

  // Ecommerce
  {
    pattern: "/ecommerce/orders",
    permission: "orders.view",
  },
  {
    pattern: "/ecommerce",
    permission: "store.manage",
  },

  // Clinic routes
  {
    pattern: "/treatments",
    permission: "treatments.manage",
  },
  {
    pattern: "/doctors",
    permission: "treatments.manage",
  },
  {
    pattern: "/departments",
    permission: "treatments.manage",
  },
  {
    pattern: "/appointments",
    permission: "appointments.view",
  },

  // The clinic ledger - the workspace's money history
  {
    pattern: "/clinics/billing",
    permission: "billing.view",
  },

  // Billing - subscription management
  {
    pattern: "/billing",
    permission: "plan.manage",
  },
  {
    pattern: "/settings/subscription",
    permission: "plan.manage",
  },

  // Settings - Organization level (org:admin only)
  {
    pattern: "/settings/organization",
    allowedWorkspaceRoles: [],
  },
  {
    pattern: "/settings/users",
    allowedWorkspaceRoles: [],
  },
  {
    pattern: "/settings/developer",
    permission: "developer.manage",
  },

  // Settings - Workspace level
  {
    pattern: "/settings/workspace",
    permission: "workspace.settings",
  },
  {
    pattern: "/settings/workspace-users",
    permission: "members.manage",
  },
  {
    pattern: "/settings/teams",
    permission: "teams.manage",
  },
  {
    pattern: "/settings/roles",
    permission: "roles.manage",
  },

  // AI agent studio
  {
    pattern: assistantPath(),
    permission: "echo.view",
  },

  // Business Hours redirects to /settings/inbox. The entry stays so the old
  // bookmark reaches the redirect instead of 404-ing.
  {
    pattern: "/settings/business-hours",
    permission: "inbox.manage",
  },

  // Settings - Workspace content
  {
    pattern: "/settings/snippets",
    allowedWorkspaceRoles: EVERYONE,
  },
  {
    pattern: "/settings/templates",
    allowedWorkspaceRoles: [WORKSPACE_ROLE.ADMIN, WORKSPACE_ROLE.MEMBER],
    permission: "channels.manage",
  },
  {
    pattern: "/settings/files",
    permission: "replies.manage",
  },
  {
    pattern: "/settings/tags",
    permission: "inbox.manage",
  },
  {
    pattern: "/settings/contact-fields",
    permission: "inbox.manage",
  },
  // Websites feed the referral channel and save with channels.manage.
  {
    pattern: "/settings/websites",
    permission: "channels.manage",
  },
  {
    pattern: "/settings/closing-notes",
    permission: "inbox.manage",
  },
  {
    pattern: "/settings/outcome-reasons",
    permission: "inbox.manage",
  },
  {
    pattern: "/settings/wrap-up",
    permission: "inbox.manage",
  },
  // The AI switch itself needs echo.mode; the page locks it without.
  {
    pattern: "/settings/ai",
    permission: "echo.view",
  },
  // Each workspace buys and spends its own credits.
  {
    pattern: "/settings/credits",
    permission: "credits.buy",
  },
  // Opening hours and SLA clocks are forms with nothing to read without
  // saving, so they need the permission their save does.
  {
    pattern: "/settings/inbox",
    permission: "inbox.manage",
  },
  {
    pattern: "/settings/sla",
    permission: "inbox.manage",
  },
  {
    pattern: "/settings/assignment",
    permission: "workspace.settings",
  },
  {
    pattern: "/settings/lifecycle",
    permission: "inbox.manage",
  },

  // Settings home lists only the pages the role can open, so it has no gate of its own
  {
    pattern: "/settings",
    allowedWorkspaceRoles: EVERYONE,
  },
];

/**
 * Find the matching route permission for a given path
 */
export function findRoutePermission(path: string): RoutePermission | undefined {
  // Normalize path - remove trailing slash
  const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path;

  // Find exact match or prefix match (for nested routes like /inbox/123/456)
  return ROUTE_PERMISSIONS.find((permission) => {
    // Exact match
    if (normalizedPath === permission.pattern) {
      return true;
    }
    // Prefix match for nested routes (e.g., /inbox/ws-123 matches /inbox)
    if (normalizedPath.startsWith(permission.pattern + "/")) {
      return true;
    }

    return false;
  });
}

/**
 * Check if a user can access a given route based on their roles
 */
export function canAccessRoute(
  path: string,
  { orgRole, workspaceRole, permissions }: RouteAccess,
): boolean {
  if (!orgRole) return false;
  if (orgRole === ORG_ROLE.ADMIN) return true;

  const route = findRoutePermission(path);

  if (!route || !workspaceRole) return false;
  if ("permission" in route) return permissions.includes(route.permission);

  return route.allowedWorkspaceRoles.includes(workspaceRole);
}

/**
 * Get the fallback route for a user when they try to access an unauthorized route
 */
export function getFallbackRoute(): string {
  return "/dashboard";
}
